import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Typography } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { Videocam, VideocamOff } from '@material-ui/icons'
import { useTranslation } from 'react-i18next'
import trackGoal from 'utils/metrika'

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  cursor: pointer;
  transition-property: transform, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.1);
  }
`

const ThumbnailWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: #1b263b;
  overflow: hidden;
`

const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const PlaceholderIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.3);
`

const StatusBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
`

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.online ? '#0FBA81' : '#FF3D71')};
  ${(p) =>
    p.online &&
    `
    animation: pulse 2s infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
`

const CardInfo = styled.div`
  padding: 10px 14px 12px;
`

const CameraName = styled(Typography)`
  && {
    font-family: 'Inter', 'Open Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #051125;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const SerialText = styled(Typography)`
  && {
    font-family: 'Open Sans', sans-serif;
    font-size: 11px;
    color: #8f9bb3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 1px;
  }
`

function CameraCard({ camera }) {
  const history = useHistory()
  const { t } = useTranslation(['cameras_view'])
  const [thumbIndex, setThumbIndex] = useState(0)

  const handleClick = () => {
    trackGoal('control_cameras_open_card', {
      camera_uuid: camera.uuid,
      is_online: Boolean(camera.is_online),
      is_linked: Boolean(
        camera.belong_to_intercom ||
          camera.intercom_uuid ||
          camera.linked_intercom_uuid
      ),
    })
    history.push(`/cameras/${camera.uuid}`)
  }

  const isOnline = camera.is_online
  const parsed = camera.parsedAddr

  const normalizePreviewUrl = (url) => {
    if (!url || typeof url !== 'string') return ''
    const trimmed = url.trim()
    if (!trimmed) return ''

    if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
      return trimmed
    }
    if (trimmed.startsWith('//')) {
      return `https:${trimmed}`
    }
    if (trimmed.startsWith('http://')) {
      return `https://${trimmed.slice('http://'.length)}`
    }
    if (trimmed.startsWith('https://')) {
      try {
        const parsed = new URL(trimmed)
        if (
          parsed.hostname === 'astral.sputnik.systems' &&
          parsed.pathname.startsWith('/routes/cameras/snapshots/')
        ) {
          return `${parsed.pathname}${parsed.search}`
        }
      } catch {
        // Ignore URL parsing error and keep original candidate.
      }
      return trimmed
    }
    if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(trimmed)) {
      return `https://${trimmed}`
    }
    if (trimmed.startsWith('/')) {
      return trimmed
    }

    return `/${trimmed.replace(/^\.?\//, '')}`
  }

  const previewCandidates = useMemo(() => {
    const cameraUuids = [camera?.camera_uuid, camera?.uuid].filter(Boolean)
    const fallbackSnapshots = cameraUuids.reduce((acc, uuid) => {
      acc.push(`https://snapshots.sputnik.systems/${uuid}/preview.jpg`)
      acc.push(`/routes/cameras/snapshots/jpg/?uuid=${uuid}`)
      acc.push(`/routes/cameras/snapshots/jpg/${uuid}/preview.jpg`)
      return acc
    }, [])

    const rawCandidates = [
      camera?.thumb,
      camera?.thumb_url,
      camera?.thumbnail_url,
      camera?.thumbnailUrl,
      camera?.preview_url,
      camera?.previewUrl,
      camera?.snapshot_url,
      camera?.snapshotUrl,
      camera?.server_thumbnails_url,
      camera?.serverThumbnailsURL,
      camera?.server_video_thumbnails_url,
      camera?.serverVideoThumbnailsURL,
      ...fallbackSnapshots,
    ]

    const unique = new Set()
    rawCandidates.forEach((candidate) => {
      const normalized = normalizePreviewUrl(candidate)
      if (normalized) unique.add(normalized)
    })

    return Array.from(unique)
  }, [camera])

  // Primary label: "Подъезд N" for entries, serial for others
  let label = camera.serial_number || camera.uuid
  if (parsed?.entrance) {
    label = t('cameras_view:entrance', { number: parsed.entrance })
  } else if (camera.address_type === 'gate') {
    label = t('cameras_view:gate')
  } else if (camera.address_type === 'house') {
    label = t('cameras_view:house_camera')
  }

  const previewSrc = previewCandidates[thumbIndex]
  const hasPreview = Boolean(previewSrc)
  const previewSourcesKey = useMemo(
    () => previewCandidates.join('|'),
    [previewCandidates]
  )

  useEffect(() => {
    setThumbIndex(0)
  }, [camera?.uuid, previewSourcesKey])

  return (
    <Card onClick={handleClick}>
      <ThumbnailWrapper>
        {hasPreview ? (
          <Thumbnail
            src={previewSrc}
            alt={label}
            onError={() => setThumbIndex((prev) => prev + 1)}
            loading="lazy"
          />
        ) : (
          <PlaceholderIcon>
            {isOnline ? (
              <Videocam style={{ fontSize: 40 }} />
            ) : (
              <VideocamOff style={{ fontSize: 40 }} />
            )}
          </PlaceholderIcon>
        )}
        <StatusBadge>
          <StatusDot online={isOnline} />
          {isOnline ? t('cameras_view:online') : t('cameras_view:offline')}
        </StatusBadge>
      </ThumbnailWrapper>
      <CardInfo>
        <CameraName>{label}</CameraName>
        <SerialText>{camera.serial_number}</SerialText>
      </CardInfo>
    </Card>
  )
}

export default CameraCard
